import { debounce, requestIdleCallback, viewTransition } from '@theme/utilities';
import { Component } from '@theme/component';

const OFFSET = 40;

/**
 * A custom element that manages a floating panel.
 */
export class FloatingPanelComponent extends Component {
  #updatePosition = async () => {
    // Wait for any view transitions to finish
    if (viewTransition.current) await viewTransition.current;

    const rect = this.getBoundingClientRect();

    if (rect.width === 0 && rect.height === 0) {
      this.#mutationObserver.takeRecords();
      return;
    }

    const viewportWidth = window.innerWidth;

    /* On n'écrit que ce qui change : réécrire la même valeur invalide quand même. */
    const topEdge = OFFSET + 'px';
    if (this.style.top !== topEdge) this.style.top = topEdge;

    let left = null;
    if (rect.right > viewportWidth) {
      left = `-${rect.right - viewportWidth + OFFSET}px`;
    } else if (rect.left < 0) {
      left = `${Math.abs(rect.left) + OFFSET}px`;
    }

    if (left !== null && this.style.left !== left) this.style.left = left;

    this.#mutationObserver.takeRecords();
  };

  #mutationObserver = new MutationObserver(this.#updatePosition);

  #resizeListener = debounce(() => {
    const parent = this.closest('details');
    const closeOnResize = this.dataset.closeOnResize === 'true';
    if (parent instanceof HTMLDetailsElement && closeOnResize) {
      parent.open = false;
      parent.removeAttribute('open');
      this.#updatePosition();
    }
  }, 100);

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('resize', this.#resizeListener);

    requestIdleCallback(() => {
      this.#updatePosition();
      this.#mutationObserver.observe(this, { attributes: true });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.#mutationObserver.disconnect();
  }
}

if (!customElements.get('floating-panel-component')) {
  customElements.define('floating-panel-component', FloatingPanelComponent);
}
